import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Parameter, ParameterList } from '@mnc/contracts';
import { api } from './api';
import { pick } from '../i18n';

export interface ParameterOption {
  value: string;
  label: string;
  flags: Record<string, boolean>;
}

/** Active values of a parameter category as picker options, plus a code → label lookup. */
export function useParameterOptions(category: string) {
  const { i18n } = useTranslation();
  const query = useQuery({ queryKey: ['parameters', category, false], queryFn: () => api.get<ParameterList>(`/api/parameters/${category}`), staleTime: 60_000 });
  const options = useMemo<ParameterOption[]>(
    () => (query.data?.items ?? []).map((p: Parameter) => ({ value: p.code, label: pick(i18n.language, p.name_en, p.name_ar), flags: p.flags })),
    [query.data, i18n.language],
  );
  const labelOf = useMemo(() => {
    const map = new Map(options.map((o) => [o.value, o.label]));
    return (code: string | null | undefined) => (code ? (map.get(code) ?? code) : '');
  }, [options]);
  return { options, labelOf, loading: query.isLoading };
}
